import React, { useState, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import './ImageDirectPage.scss';
import { getTreasuryTotalValueUSD } from '../../Home/TreasuryPage/treasuryValue';

// Constants
const REDDICKS_RESOURCE_ADDRESS = 'resource_rdx1t42hpqvsk4t42l6aw09hwphd2axvetp6gvas9ztue0p30f4hzdwxrp';
const ASTROLESCENT_API_URL = 'https://api.astrolescent.com/partner/reddicks/prices';
const JUSTLOCK_API_URL = `https://api.justlock.io/resource/${REDDICKS_RESOURCE_ADDRESS}`;
const OCISWAP_API_URL = `https://api.ociswap.com/tokens/${REDDICKS_RESOURCE_ADDRESS}`;

const ImageDirectPage = () => {
  const [tokenData, setTokenData] = useState(null);
  const [lockData, setLockData] = useState(null);
  const [ociswapData, setOciswapData] = useState(null);
  const [treasuryValue, setTreasuryValue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  
  const cardRef = useRef(null);
  const hasRenderedRef = useRef(false);

  // Helper functions
  const formatNumber = (num, decimals = 2) => {
    if (typeof num !== 'number') return 'N/A';
    return num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };
  
  const formatCurrency = (value) => {
    if (typeof value !== 'number' || isNaN(value)) {
      return '$0.00';
    }
    return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  
  const formatLargeNumber = (num) => {
    if (typeof num !== 'number' || isNaN(num)) return 'N/A';
    if (num >= 1e9) return (num / 1e9).toFixed(2) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(1) + 'K';
    return num.toFixed(0);
  };

  const formatPrice = (price) => {
    if (typeof price !== 'number') return 'N/A';
    if (price < 0.0001 && price > 0) return price.toExponential(2);
    if (price < 1) return price.toFixed(6);
    return formatNumber(price, 2);
  };

  const formatPercentage = (diff) => {
    if (typeof diff !== 'number') return { value: 'N/A', className: '' };
    const value = (diff * 100).toFixed(2);
    const className = diff > 0 ? 'positive' : diff < 0 ? 'negative' : 'neutral';
    return { value: `${value}%`, className };
  };

  // Ensure assets are ready before capture
  const waitForCardAssets = async () => {
    const element = cardRef.current;
    if (!element) return;

    const waitForImages = () => {
      const images = Array.from(element.querySelectorAll('img'));
      if (images.length === 0) return Promise.resolve();
      return Promise.all(images.map(img => {
        if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
          return Promise.resolve();
        }
        return new Promise(resolve => {
          const done = () => {
            img.removeEventListener('load', done);
            img.removeEventListener('error', done);
            resolve();
          };
          img.addEventListener('load', done);
          img.addEventListener('error', done);
        });
      }));
    };

    const waitForFonts = () => {
      if (document.fonts && document.fonts.ready) {
        return document.fonts.ready.catch(() => {});
      }
      return Promise.resolve();
    };

    await Promise.all([waitForImages(), waitForFonts()]);
  };

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch price data
        const pricePromise = fetch(ASTROLESCENT_API_URL)
          .then(res => {
            if (!res.ok) throw new Error('Failed to fetch price data from Astrolescent');
            return res.json();
          })
          .then(priceData => {
            const reddicksPriceData = priceData[REDDICKS_RESOURCE_ADDRESS];
            if (!reddicksPriceData) {
              throw new Error('REDDICKS token data not found in Astrolescent response');
            }
            setTokenData(reddicksPriceData);
          });

        // Fetch lock data
        const lockPromise = fetch(JUSTLOCK_API_URL)
          .then(res => {
            if (!res.ok) {
              console.warn('Failed to fetch lock data from JustLock, proceeding without it.');
              return null;
            }
            return res.json();
          })
          .then(lockJson => setLockData(lockJson));
        
        // Fetch data from OCISwap
        const ociswapPromise = fetch(OCISWAP_API_URL)
            .then(res => {
                if (!res.ok) {
                    console.warn('Failed to fetch data from OCISwap, proceeding without it.');
                    return null;
                }
                return res.json();
            })
            .then(ociswapJson => setOciswapData(ociswapJson));

        // Fetch Treasury Value
        const treasuryPromise = getTreasuryTotalValueUSD()
          .then(value => setTreasuryValue(value))
          .catch(err => {
            console.warn('Failed to fetch treasury value:', err.message);
            // Set to a specific value or null to indicate failure but not block rendering
            setTreasuryValue('Error'); 
          });

        await Promise.all([pricePromise, lockPromise, ociswapPromise, treasuryPromise]);

        setLoading(false);
      } catch (err) {
        console.error("Error fetching token data:", err);
        setError(err.message || 'Failed to load token data.');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Generate image when data is loaded
  useEffect(() => {
    const generate = async () => {
      if (hasRenderedRef.current) return;
      if (loading || error || !tokenData || !lockData || !ociswapData || treasuryValue === null || !cardRef.current) return;

      try {
        await waitForCardAssets();

        const element = cardRef.current;
        const originalStyle = {
          width: element.style.width,
          position: element.style.position,
          visibility: element.style.visibility,
          left: element.style.left,
          top: element.style.top,
          opacity: element.style.opacity
        };

        // Ensure the element has non-zero dimensions
        let measuredWidth = element.getBoundingClientRect().width;
        if (!measuredWidth || measuredWidth < 1) {
          measuredWidth = 600; // fallback width in px
          element.style.width = measuredWidth + 'px';
        }
        // Make sure it's renderable
        element.style.position = 'static';
        element.style.visibility = 'visible';
        element.style.opacity = '1';

        // Slight delay to allow layout to settle
        await new Promise(r => setTimeout(r, 50));

        hasRenderedRef.current = true;

        const canvas = await html2canvas(element, {
          backgroundColor: 'rgba(20, 22, 40, 0.95)',
          useCORS: true,
          imageTimeout: 15000,
          scale: 2,
          onclone: (clonedDoc) => {
            const clonedCard = clonedDoc.querySelector('.stats-card-direct');
            if (clonedCard) {
              clonedCard.style.display = 'block';
              clonedCard.style.visibility = 'visible';
              clonedCard.style.position = 'static';
              clonedCard.style.width = measuredWidth + 'px';
              clonedCard.style.opacity = '1';
            }
          }
        });

        const url = canvas.toDataURL('image/png');
        setImageUrl(url);

        if (window.location.pathname === '/live-card/card.png') {
          const link = document.createElement('a');
          link.download = 'reddicks-stats.png';
          link.href = url;
          link.click();
        }
      } catch (err) {
        console.error('Error generating card image:', err);
        setError('Failed to generate card image: ' + err.message);
      }
    };

    generate();
  }, [loading, error, tokenData, lockData, ociswapData, treasuryValue]);

  // Calculate values for display
  const totalSupply = tokenData?.totalSupply ? parseFloat(tokenData.totalSupply) : 0;
  const lockedAmount = lockData?.lock_summary?.amount ? parseFloat(lockData.lock_summary.amount) : 0;
  const lockedPercentage = totalSupply > 0 && lockedAmount > 0 ? ((lockedAmount / totalSupply) * 100).toFixed(2) : 0;

  const priceUSD = tokenData?.tokenPriceUSD;
  const priceXRD = tokenData?.tokenPriceXRD;
  const diff7DaysUSD = formatPercentage(tokenData?.diff7DaysUSD);
  const diff7DaysXRD = formatPercentage(tokenData?.diff7Days);
  const diff24HUSD = formatPercentage(tokenData?.diff24HUSD);
  const diff24HXRD = formatPercentage(tokenData?.diff24H);

  // Extract Market Cap Data
  const marketCapUSDString = ociswapData?.market_cap?.circulating?.usd?.now;
  const marketCapUSD = marketCapUSDString ? parseFloat(marketCapUSDString) : null;

  // If we have the image, show only that
  if (imageUrl) {
    // For /live-card/card.png route, show just the image for direct access
    if (window.location.pathname === '/live-card/card.png') {
      return (
        <div className="image-only-container">
          <img src={imageUrl} alt="Reddicks Stats" className="card-image" style={{display: 'block', width: '100%'}} />
        </div>
      );
    }
    
    // For /live-card route, show image with some context
    return (
      <div className="card-image-container">
        <img src={imageUrl} alt="Reddicks Stats" className="card-image" />
        <div className="image-info">
          <p>Direct image URL for bots:</p>
          <a href="/live-card/card.png" className="direct-link" target="_blank" rel="noopener noreferrer">
            {window.location.origin}/live-card/card.png
          </a>
        </div>
      </div>
    );
  }

  // Show loading message if we're still loading
  if (loading) {
    return <div className="card-loading">Loading Reddicks stats...</div>;
  }

  // Show error message if there was an error
  if (error) {
    return <div className="card-error">Error: {error}</div>;
  }

  // Render the card (this will be captured by html2canvas) - HIDDEN for card.png route
  if (window.location.pathname === '/live-card/card.png') {
    return (
      <div style={{position: 'absolute', left: '-9999px', top: 0}}>
        <div className="stats-card-direct" ref={cardRef} style={{ width: '600px', boxSizing: 'border-box', visibility: 'hidden' }}>
          {/* Card content */}
          {tokenData && (
            <>
              <div className="card-header">
                <img src={tokenData.iconUrl} alt={`${tokenData.name} logo`} className="token-icon" />
                <div className="header-content">
                  <h3>
                    <span>{tokenData.symbol}</span>
                    <span>Stats</span>
                  </h3>
                  <div className="tagline">Come for the name, stay for the tech</div>
                </div>
                <div className="live-indicator"></div>
              </div>
              
              <div className="card-body">
                {/* Price Section */}
                <div className="stats-section price-section">
                  <div className="card-section-header">PRICES</div>
                  <div className="stat-row price-row">
                    <div className="stat-item">
                      <span className="label">USD Price:</span>
                      <span className="value">${formatPrice(priceUSD)}</span>
                    </div>
                    <div className="stat-item">
                      <span className="label">24h Change:</span>
                      <span className={`value ${diff24HUSD.className}`}>
                        {diff24HUSD.value}
                        <span className="trend-arrow">{diff24HUSD.className === 'positive' ? '↑' : diff24HUSD.className === 'negative' ? '↓' : ''}</span>
                      </span>
                    </div>
                    <div className="stat-item">
                      <span className="label">7d Change:</span>
                      <span className={`value ${diff7DaysUSD.className}`}>
                        {diff7DaysUSD.value}
                        <span className="trend-arrow">{diff7DaysUSD.className === 'positive' ? '↑' : diff7DaysUSD.className === 'negative' ? '↓' : ''}</span>
                      </span>
                    </div>
                  </div>
                  <div className="stat-row price-row">
                    <div className="stat-item">
                      <span className="label">XRD Price:</span>
                      <span className="value">{formatPrice(priceXRD)}</span>
                    </div>
                    <div className="stat-item">
                      <span className="label">24h Change:</span>
                      <span className={`value ${diff24HXRD.className}`}>
                        {diff24HXRD.value}
                        <span className="trend-arrow">{diff24HXRD.className === 'positive' ? '↑' : diff24HXRD.className === 'negative' ? '↓' : ''}</span>
                      </span>
                    </div>
                    <div className="stat-item">
                      <span className="label">7d Change:</span>
                      <span className={`value ${diff7DaysXRD.className}`}>
                        {diff7DaysXRD.value}
                        <span className="trend-arrow">{diff7DaysXRD.className === 'positive' ? '↑' : diff7DaysXRD.className === 'negative' ? '↓' : ''}</span>
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="stats-divider"></div>
                
                {/* Market Stats Section */}
                <div className="stats-section">
                  <div className="card-section-header">MARKET STATS & HOLDINGS</div>
                  <div className="stat-row">
                    <div className="stat-item">
                      <span className="label">Market Cap USD:</span>
                      <span className="value">${formatLargeNumber(marketCapUSD)}</span>
                    </div>
                    <div className="stat-item">
                      <span className="label">Treasury Value:</span>
                      <span className="value">{treasuryValue === 'Error' ? 'N/A' : formatCurrency(treasuryValue)}</span>
                    </div>
                    <div className="stat-item">
                      <span className="label">Next Charity Donation:</span>
                      <span className="value">$5,875.20</span>
                    </div>
                  </div>
                  
                  <div className="stat-row supply-row">
                    <div className="stat-item">
                      <span className="label">Total Supply:</span>
                      <span className="value">{formatNumber(totalSupply, 0)}</span>
                    </div>
                    <div className="stat-item">
                      <span className="label">Total Locked:</span>
                      <span className="value">
                        {lockData ? `${formatNumber(lockedAmount, 0)} (${lockedPercentage}%)` : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="card-footer">
                <div className="timestamp">
                  Last updated: {new Date().toLocaleString()}
                </div>
                <div className="website-link">reddicks.meme</div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // Regular display for the /live-card route
  return (
    <div className="card-render-container">
      <div className="stats-card-direct" ref={cardRef}>
        {tokenData && (
          <>
            <div className="card-header">
              <img src={tokenData.iconUrl} alt={`${tokenData.name} logo`} className="token-icon" />
              <div className="header-content">
                <h3>
                  <span>{tokenData.symbol}</span>
                  <span>Stats</span>
                </h3>
                <div className="tagline">Come for the name, stay for the tech</div>
              </div>
              <div className="live-indicator"></div>
            </div>
            
            <div className="card-body">
              {/* Price Section */}
              <div className="stats-section price-section">
                <div className="card-section-header">PRICES</div>
                <div className="stat-row price-row">
                  <div className="stat-item">
                    <span className="label">USD Price:</span>
                    <span className="value">${formatPrice(priceUSD)}</span>
                  </div>
                  <div className="stat-item">
                    <span className="label">24h Change:</span>
                    <span className={`value ${diff24HUSD.className}`}>
                      {diff24HUSD.value}
                      <span className="trend-arrow">{diff24HUSD.className === 'positive' ? '↑' : diff24HUSD.className === 'negative' ? '↓' : ''}</span>
                    </span>
                  </div>
                  <div className="stat-item">
                    <span className="label">7d Change:</span>
                    <span className={`value ${diff7DaysUSD.className}`}>
                      {diff7DaysUSD.value}
                      <span className="trend-arrow">{diff7DaysUSD.className === 'positive' ? '↑' : diff7DaysUSD.className === 'negative' ? '↓' : ''}</span>
                    </span>
                  </div>
                </div>
                <div className="stat-row price-row">
                  <div className="stat-item">
                    <span className="label">XRD Price:</span>
                    <span className="value">{formatPrice(priceXRD)}</span>
                  </div>
                  <div className="stat-item">
                    <span className="label">24h Change:</span>
                    <span className={`value ${diff24HXRD.className}`}>
                      {diff24HXRD.value}
                      <span className="trend-arrow">{diff24HXRD.className === 'positive' ? '↑' : diff24HXRD.className === 'negative' ? '↓' : ''}</span>
                    </span>
                  </div>
                  <div className="stat-item">
                    <span className="label">7d Change:</span>
                    <span className={`value ${diff7DaysXRD.className}`}>
                      {diff7DaysXRD.value}
                      <span className="trend-arrow">{diff7DaysXRD.className === 'positive' ? '↑' : diff7DaysXRD.className === 'negative' ? '↓' : ''}</span>
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="stats-divider"></div>
              
              {/* Market Stats Section */}
              <div className="stats-section">
                <div className="card-section-header">MARKET STATS & HOLDINGS</div>
                <div className="stat-row">
                  <div className="stat-item">
                    <span className="label">Market Cap USD:</span>
                    <span className="value">${formatLargeNumber(marketCapUSD)}</span>
                  </div>
                  <div className="stat-item">
                    <span className="label">Treasury Value:</span>
                    <span className="value">{treasuryValue === 'Error' ? 'N/A' : formatCurrency(treasuryValue)}</span>
                  </div>
                  <div className="stat-item">
                    <span className="label">Next Charity Donation:</span>
                    <span className="value">$5,875.20</span>
                  </div>
                </div>
                
                <div className="stat-row supply-row">
                  <div className="stat-item">
                    <span className="label">Total Supply:</span>
                    <span className="value">{formatNumber(totalSupply, 0)}</span>
                  </div>
                  <div className="stat-item">
                    <span className="label">Total Locked:</span>
                    <span className="value">
                      {lockData ? `${formatNumber(lockedAmount, 0)} (${lockedPercentage}%)` : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="card-footer">
              <div className="timestamp">
                Last updated: {new Date().toLocaleString()}
              </div>
              <div className="website-link">reddicks.meme</div>
            </div>
          </>
        )}
      </div>
      <div className="loading-message">
        Generating PNG image... This may take a moment.
      </div>
    </div>
  );
};

export default ImageDirectPage; 
